import { useContext, useEffect } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import EntitySelector from '@components/EntitySelector'

import type { TemplatePageCategoryModel } from '@models/types'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import { confirmDelete } from '@functions/entity'

import useTemplatePageCategory, { useTemplatePageCategoryForm } from '@hooks/useTemplatePageCategory'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

const MODAL_KEY = 'CreateOrEditTemplatePageCategoryModal'

type ModalCallbacks = {
  closeModal: () => void,
  createTemplatePageCategory: (payload: HandleCallbackActionParams) => Promise<object>,
  updateTemplatePageCategory: (payload: HandleCallbackActionParams) => Promise<object>,
}

type CreateOrEditTemplatePageCategoryModalParams = {
  callbacks: ModalCallbacks,
  createFn: Function,
  entityState: Partial<TemplatePageCategoryModel>,
  updateFn: Function,
}

const createOrEditTemplatePageCategory = (params: CreateOrEditTemplatePageCategoryModalParams) => {
  const {
    callbacks, createFn, entityState, updateFn,
  } = params

  const { closeModal, createTemplatePageCategory, updateTemplatePageCategory } = callbacks || {}
  const [text, actionFn] = entityState.id ? ['Updated', updateFn] : ['Created', createFn]

  const templatePageCategoryPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    requestOptions: defaultRequestOptions.templatePageCategory,
    toastText: `Template Category ${text}`,
  }

  // @ts-expect-error TS2345
  if (entityState.id) return updateTemplatePageCategory(templatePageCategoryPayload)

  // @ts-expect-error TS2345
  return createTemplatePageCategory(templatePageCategoryPayload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  modalKey?: string,
  showModal: boolean,
}

const CreateOrEditTemplatePageCategoryModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { templatePageCategory }: { templatePageCategory?: TemplatePageCategoryModel} = modalPayload

  const templatePageCategoryPayload = useTemplatePageCategory(templatePageCategory)

  const {
    callbacks: {
      createTemplatePageCategory: createFn,
      deleteTemplatePageCategory: deleteFn,
      updateTemplatePageCategory: updateFn,
    },
    creating,
    updating,
  } = templatePageCategoryPayload

  const formPayload = useTemplatePageCategoryForm(templatePageCategory || {}, { validateOn: 'change' })

  const {
    entityState,
    entityState: {
      allow_category_mix,
      brand_id,
      global,
      hidden,
      id,
      title,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = formPayload

  useEffect(() => {
    if (global) setEntityState({ brand_id: '' })
  }, [global])

  const confirmDeletePayload = {
    callbacks: {
      afterAction: closeModal,
      deleteFn,
    },
    strings: { deleteString: 'archive' as const, entityName: 'Template Category' },
  }
  const updateOrCreateButtonText = id ? 'Save Template Category' : 'Create Template Category'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Template Category" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" type="text" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="Allow Mixing" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select data-value-type="boolean" name="allow_category_mix" value={allow_category_mix} {...handlers}>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Field label="Global" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select data-value-type="boolean" name="global" value={global} {...handlers}>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          {!global && (
            <EntitySelector
              callbacks={{
                selectItem: brand => setEntityState({ brand_id: brand ? brand.id : null, owner_type: 'Brand' }),
              }}
              entityKey="brands"
              errorMessage={errors.brand_id}
              label="* Brand"
              marginTop="large"
              selectedItemId={brand_id}
            />
          )}

          {id && templatePageCategory?.hidden && (
            <Form.Field label="Active" marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <select data-value-type="boolean" name="hidden" value={hidden} {...handlers}>
                <option value="true">No</option>
                <option value="false">Yes</option>
              </select>
            </Form.Field>
          )}

          {id && !templatePageCategory?.hidden && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                onClick={() => confirmDelete(confirmDeletePayload)}
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                size="medium"
              >
                Archive Category
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          onClick={() => createOrEditTemplatePageCategory({
            entityState, callbacks, createFn, updateFn,
          })}
          size="large"
        >
          {saveEnabled ? updateOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditTemplatePageCategoryModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
